/**
 * SupportAtHomeHelpers.js – Shared constants and helpers for Support at Home flow.
 * Used by SupportAtHomePage, StrategiesSection, and ServiceBuilderSection.
 */
import { PENSION_RATES } from '../../constants/index.ts';
import {
    computeDisposalCgt,
    taxContextFromAssessableIncomes,
    type DisposalTaxContext,
} from '../../../../shared/src/calculation/disposalCgt.ts';
import { applyDownsizeToFactFind } from './supportAtHomeFactFindMutators.tsx';
import {
    debitCgtFromOwnershipPool,
    debitFromPersonalBankFirst,
} from '../../utils/entityFundingUtils.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, AssetType } from '../../../../shared/src/calculation/factFindTypes.ts';
import { INVESTMENT_PROFILES, ASSET_CLASS_ASSUMPTIONS } from '../../../../shared/src/calculation/investmentProfiles.ts';

type DraftAsset = FactFindAsset;

export { applyDownsizeToFactFind };

// Re-export centrally maintained Age Pension rates for Support at Home flows.
export { PENSION_RATES };

export const DOWNSIZER_CONTRIBUTION_CAP = 300000;

// Re-export the shared ui CurrencyInput so existing `from './SupportAtHomeHelpers.tsx'` imports
// keep working — this used to be its own hand-rolled duplicate.
export { default as CurrencyInput } from '../../components/ui/CurrencyInput.tsx';

/** Calc-engine `financials` result bag — only the two fields this helper reads (see calculationApi.ts's `Financials = Record<string, unknown>`). */
export interface FinancialsForCgt {
    clientAssessableIncome?: number;
    partnerAssessableIncome?: number;
    /** Household tax position for the year, used to tax a capital gain — see disposalCgt.ts. */
    taxContext?: DisposalTaxContext;
    [key: string]: unknown;
}

/**
 * CGT on an asset sale funding a Support at Home strategy.
 * Delegates to the single shared CGT engine (shared/src/calculation/disposalCgt.ts) — this previously
 * held a fourth copy of the same math.
 */
export const calculateCgtForStrategy = (asset: DraftAsset, saleAmount: number, beforeFinancials: FinancialsForCgt | undefined): number => {
    const value = parseFloat(String(asset.value)) || 0;
    if (!value || saleAmount <= 0) return 0;

    // `taxContext` comes from calculateFinancials; the fallback covers a payload predating it.
    const taxContext =
        beforeFinancials?.taxContext ??
        taxContextFromAssessableIncomes(
            beforeFinancials?.clientAssessableIncome || 0,
            beforeFinancials?.partnerAssessableIncome || 0,
            asset.ownership === 'joint' || asset.ownership === 'partner',
        );

    // DraftAsset is structurally compatible with FactFindAsset (see types/formDraft.ts); the shared
    // calc-engine fn defensively coerces internally regardless, so this cast is safe (see
    // factFindTypes.ts header comment).
    return computeDisposalCgt({
        taxContext,
        asset: asset as unknown as FactFindAsset,
        salePrice: saleAmount,
        // Brokerage on a share sale is immaterial at this level of estimate.
        sellingCosts: 0,
        originalAssetValue: value,
    }).estimatedCGT;
};

// applyDownsizeToFactFind — see supportAtHomeFactFindMutators.js (re-exported above)

/** A single asset earmarked to fund a renovate/upgrade property strategy. */
export interface PropertyStrategyFundingSource {
    assetId: string | number;
    assetType: string;
    amount: number | string;
}

export interface RenovateDetails {
    renovationAmount: number | string;
    fundingSources?: PropertyStrategyFundingSource[];
    [key: string]: unknown;
}

export interface UpgradeDetails {
    currentHomeValue?: number | string;
    newHomePurchasePrice: number | string;
    sellingCosts?: number | string;
    fundingSources?: PropertyStrategyFundingSource[];
    [key: string]: unknown;
}

// Apply renovate strategy to factFind
export const applyRenovateToFactFind = (factFind: DraftFactFind, details: RenovateDetails, beforeFinancials: FinancialsForCgt | undefined = {}): DraftFactFind => {
    const modified: DraftFactFind = structuredClone(factFind);
    const renovationAmount = parseFloat(String(details.renovationAmount)) || 0;
    if (renovationAmount <= 0) return modified;

    let totalAllocated = 0;
    const fundingSources = details.fundingSources || [];

    fundingSources.forEach(funding => {
        const assetId = funding.assetId;
        const assetType = funding.assetType;
        const amount = parseFloat(String(funding.amount)) || 0;
        if (amount <= 0) return;

        totalAllocated += amount;

        const assetList = modified.assets?.[assetType];
        if (assetList && Array.isArray(assetList)) {
            const asset = assetList.find(a => String(a.id) === String(assetId));
            if (asset) {
                const assetValue = parseFloat(String(asset.value)) || 0;
                const saleAmount = Math.min(amount, assetValue);

                if (['shares', 'investmentProperty'].includes(assetType) && asset.costBase) {
                    const cgt = calculateCgtForStrategy(asset, saleAmount, beforeFinancials);
                    debitCgtFromOwnershipPool(modified.assets, cgt, asset.ownership);
                }

                const newValue = Math.max(0, assetValue - saleAmount);
                if (newValue > 0) {
                    asset.value = newValue;
                } else {
                    const index = assetList.indexOf(asset);
                    assetList.splice(index, 1);
                }
            }
        }
    });

    const remainder = renovationAmount - totalAllocated;
    if (remainder > 0) {
        debitFromPersonalBankFirst(modified.assets, remainder);
    }

    if (modified.home) {
        const currentHomeValue = parseFloat(String(modified.home.value)) || 0;
        modified.home.value = currentHomeValue + renovationAmount;
    } else {
        modified.home = { value: renovationAmount };
    }

    return modified;
};

// Apply upgrade home strategy to factFind
export const applyUpgradeToFactFind = (factFind: DraftFactFind, details: UpgradeDetails, beforeFinancials: FinancialsForCgt | undefined = {}): DraftFactFind => {
    const modified: DraftFactFind = structuredClone(factFind);
    const currentHomeValue = parseFloat(String(details.currentHomeValue || factFind.home?.value)) || 0;
    const newHomePurchasePrice = parseFloat(String(details.newHomePurchasePrice)) || 0;
    const sellingCosts = parseFloat(String(details.sellingCosts)) || 0;

    if (newHomePurchasePrice <= currentHomeValue) return modified;

    const netProceedsFromSale = Math.max(0, currentHomeValue - sellingCosts);
    const additionalFundsNeeded = newHomePurchasePrice - netProceedsFromSale;

    if (additionalFundsNeeded <= 0) {
        if (modified.home) {
            modified.home.value = newHomePurchasePrice;
        } else {
            modified.home = { value: newHomePurchasePrice };
        }

        const surplus = Math.abs(additionalFundsNeeded);
        if (surplus > 0 && modified.assets?.bankAccounts && modified.assets.bankAccounts.length > 0) {
            const firstBankAccount = modified.assets.bankAccounts[0];
            const currentBalance = parseFloat(String(firstBankAccount.value)) || 0;
            firstBankAccount.value = currentBalance + surplus;
        }
        return modified;
    }

    const fundingSources = details.fundingSources || [];
    let totalAllocated = 0;

    fundingSources.forEach(funding => {
        const assetId = funding.assetId;
        const assetType = funding.assetType;
        const amount = parseFloat(String(funding.amount)) || 0;
        if (amount <= 0) return;

        totalAllocated += amount;

        const assetList = modified.assets?.[assetType];
        if (assetList && Array.isArray(assetList)) {
            const asset = assetList.find(a => String(a.id) === String(assetId));
            if (asset) {
                const assetValue = parseFloat(String(asset.value)) || 0;
                const saleAmount = Math.min(amount, assetValue);

                if (['shares', 'investmentProperty'].includes(assetType) && asset.costBase) {
                    const cgt = calculateCgtForStrategy(asset, saleAmount, beforeFinancials);
                    debitCgtFromOwnershipPool(modified.assets, cgt, asset.ownership);
                }

                const newValue = Math.max(0, assetValue - saleAmount);
                if (newValue > 0) {
                    asset.value = newValue;
                } else {
                    const index = assetList.indexOf(asset);
                    assetList.splice(index, 1);
                }
            }
        }
    });

    const remainder = additionalFundsNeeded - totalAllocated;
    if (remainder > 0) {
        debitFromPersonalBankFirst(modified.assets, remainder);
    }

    if (modified.home) {
        modified.home.value = newHomePurchasePrice;
    } else {
        modified.home = { value: newHomePurchasePrice };
    }

    return modified;
};

export interface SupportAssetOption {
    id: AssetType;
    label: string;
    defaults: {
        incomeRate: number;
        growthRate: number;
        frankingCredit?: number;
    };
}

export const SUPPORT_ASSET_OPTIONS: SupportAssetOption[] = [
    // Property getters throughout so a licensee's capital market assumptions apply at use time.
    { id: 'bankAccounts', label: 'Bank account', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.bankIncomePct; }, growthRate: 0 } },
    { id: 'termDeposits', label: 'Term deposit', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.termDepositIncomePct; }, growthRate: 0 } },
    { id: 'shares', label: 'Shares / Managed funds', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.sharesIncomePct; }, get growthRate() { return ASSET_CLASS_ASSUMPTIONS.sharesGrowthPct; }, get frankingCredit() { return ASSET_CLASS_ASSUMPTIONS.sharesFrankingPct; } } },
    // Balanced presets from the one published table — property getters so the rate is read at USE
    // time and a licensee's own capital market assumptions apply (see investmentProfiles.ts).
    { id: 'superannuation', label: 'Superannuation', defaults: { incomeRate: 0, get growthRate() { return INVESTMENT_PROFILES.balanced.accumulationNetPct; } } },
    { id: 'accountBasedPensions', label: 'Account-based pension', defaults: { incomeRate: 0, get growthRate() { return INVESTMENT_PROFILES.balanced.pensionNetPct; } } },
    { id: 'investmentProperty', label: 'Investment property', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.propertyYieldPct; }, get growthRate() { return ASSET_CLASS_ASSUMPTIONS.propertyGrowthPct; } } },
    { id: 'personalAssets', label: 'Personal asset', defaults: { incomeRate: 0, growthRate: 0 } },
    { id: 'other', label: 'Other asset', defaults: { incomeRate: 0, growthRate: 0 } }
];
